import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';

export interface DeterministicProcessFormValues {
  name: string;
  description?: string;
  enabled: boolean;
}

export interface DeterministicProcessFormInitialValues {
  name: string;
  description: string;
  enabled: boolean;
  handlerKey: string | null;
  triggerType: string;
}

interface Work4MeDeterministicProcessFormProps {
  initialValues: DeterministicProcessFormInitialValues;
  isSubmitting: boolean;
  onSubmit: (values: DeterministicProcessFormValues) => Promise<void>;
}

export function Work4MeDeterministicProcessForm({
  initialValues,
  isSubmitting,
  onSubmit,
}: Work4MeDeterministicProcessFormProps) {
  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description);
  const [enabled, setEnabled] = useState(initialValues.enabled);

  useEffect(() => {
    setName(initialValues.name);
    setDescription(initialValues.description);
    setEnabled(initialValues.enabled);
  }, [initialValues]);

  const canSubmit = name.trim().length > 0 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    await onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      enabled,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="det-process-name">Name</Label>
        <Input id="det-process-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
      </div>

      <div>
        <Label htmlFor="det-process-description">Description</Label>
        <Textarea
          id="det-process-description"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="mt-1 resize-none"
        />
      </div>

      <div>
        <Label>Handler key</Label>
        <p className="mt-1 text-sm font-mono text-muted-foreground">{initialValues.handlerKey ?? '—'}</p>
      </div>

      <div className="flex items-center justify-between rounded border p-3">
        <div>
          <p className="text-sm font-medium">Enabled</p>
          <p className="text-xs text-muted-foreground">Disabled processes stay stored but will not trigger.</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} disabled={isSubmitting} />
      </div>

      <Button className="w-full gap-2" onClick={handleSubmit} disabled={!canSubmit}>
        <Save className="h-4 w-4" />
        {isSubmitting ? 'Saving...' : 'Save Process'}
      </Button>
    </div>
  );
}
